/**
 * Between a session and the GM's asset library: reading what a version holds
 * to copy it in, and putting what was made at the table into the library.
 *
 * The table never READS the library to play (the user's ruling: copies, not
 * links) - these run only when the GM asks: copy in, update a copy, save a
 * palette, a map, a scene or a sheet at the table. Every failure throws a
 * sentence for the caller's toast.
 */
import { readFileVersion, readVersion, saveVersion, writeFolder, type Library } from './assetStore';
import { extensionFor } from './engine/assets/fileKinds';
import { KINDS, fileNameFor, type ContentOf } from './engine/assets/kinds';
import { storeThumb } from './assetThumbs';
import { newAsset, newFolderRow } from './engine/assets/rows';
import { uniqueName } from './engine/assets/tree';
import { fullSentence, wouldExceed, type BackendName } from './engine/assets/quota';
import { ROOT, type AssetKind, type AssetRow, type DocKind, type Note, type Origin } from './engine/assets/types';
import { ownedRow } from './engine/assets/provenance';
import type { ArmouryKind } from './engine/assets/armouryKinds';
import type { Terrain } from './engine/battlefield/presets';
import type { ParkedScene } from './engine/scenes/types';
import type { Character } from './engine/character/types';
import { mintId } from './utils/mintUnique';

export interface LibraryAccess {
  uid: string;
  lib: Library;
  backendName: BackendName;
}

/** An asset's current version, decoded as its kind; throws the reader's sentence when it does not read. */
async function readCurrent<K extends DocKind>(access: LibraryAccess, row: AssetRow, kind: K): Promise<ContentOf[K]> {
  const { content, error } = KINDS[kind].decode(await readVersion(access.uid, row.id, row.version));
  if (!content) throw new Error(`${row.name} could not be read: ${error}`);
  return content as ContentOf[K];
}

/** A map asset's current terrain. */
export async function readMapAsset(access: LibraryAccess, row: AssetRow): Promise<Terrain> {
  return (await readCurrent(access, row, 'map')).terrain;
}

/** A palette asset's surfaces and materials. */
export const readPaletteAsset = (access: LibraryAccess, row: AssetRow) => readCurrent(access, row, 'palette');

/**
 * A scene asset as it was saved - its state too - with fresh ids for the wings, under its card's name: a scene has
 * ONE name (2026-10-20), so a card renamed in the library comes in renamed, wherever it is read.
 */
export async function readSceneAsset(access: LibraryAccess, row: AssetRow): Promise<ParkedScene> {
  const parked = await readCurrent(access, row, 'scene');
  return { ...parked, record: { ...parked.record, name: row.name } };
}

/** A character asset's sheet. */
export const readCharacterAsset = (access: LibraryAccess, row: AssetRow): Promise<Character> =>
  readCurrent(access, row, 'character');

/** One of the Armoury's assets (plan armoury): an effect, a template, a weapon..., as its kind decodes it. */
export const readArmouryAsset = <K extends ArmouryKind>(access: LibraryAccess, row: AssetRow, kind: K) =>
  readCurrent(access, row, kind);

/** An image or handout asset's current file, named after the asset: what a handout is posted from. */
export async function readFileAsset(access: LibraryAccess, row: AssetRow): Promise<File> {
  const blob = await readFileVersion(access.uid, row.id, row.version);
  return new File([blob], fileNameFor(row.name, extensionFor(blob.type)), { type: blob.type });
}

/** The folder things made at a table land in - "From session ABC123" - made once. */
async function sessionFolder(access: LibraryAccess, label: string): Promise<string> {
  const name = `From session ${label}`;
  const found = access.lib.folders.find((f) => f.parentId === ROOT && f.name === name);
  if (found) return found.id;
  const folder = newFolderRow(mintId('f'), name, ROOT, Date.now());
  await writeFolder(access.uid, folder);
  return folder.id;
}

/** Save a version of `row` (or a new asset in the session's folder), checking the room first. */
async function saveToLibrary(access: LibraryAccess, row: AssetRow, text: string, note: Note): Promise<AssetRow> {
  if (wouldExceed(access.lib.assets, text.length, access.backendName, access.lib.foreignBytes))
    throw new Error(fullSentence(access.backendName));
  const saved = await saveVersion({ uid: access.uid, asset: row, text, note, now: Date.now() });
  void storeThumb(access.uid, saved, text);
  return saved;
}

/** A new asset made at the table, in "From session <label>". */
export async function newFromTable(
  access: LibraryAccess,
  kind: AssetKind,
  wanted: string,
  text: string,
  label: string,
): Promise<AssetRow> {
  const folderId = await sessionFolder(access, label);
  const taken = access.lib.assets.filter((a) => a.folderId === folderId && !a.deletedAt).map((a) => a.name);
  const row = newAsset(mintId('a'), kind, uniqueName(taken, wanted), folderId, Date.now());
  return saveToLibrary(access, row, text, { kind: 'table', sessionName: label });
}

/**
 * Something made or changed at the table, into the library: the next version of `origin`'s asset when this
 * library still owns it, else a new asset in "From session <label>" (a foreign or missing origin makes a new one).
 */
export async function saveMadeAtTable(
  access: LibraryAccess,
  made: { kind: AssetKind; name: string; text: string; origin?: Origin | null },
  label: string,
): Promise<AssetRow> {
  const row = ownedRow(access.lib.assets, made.origin);
  return row
    ? saveToLibrary(access, row, made.text, { kind: 'table', sessionName: label })
    : newFromTable(access, made.kind, made.name, made.text, label);
}
